import { useState } from "react";
import Card from "@mui/material/Card";
import Drawer from "@mui/material/Drawer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDProgress from "components/MDProgress";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";

function BootcampPathwayTimeline({ pathway }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);

  const handleStepClick = (step) => {
    if (step.status !== "locked") {
      setSelectedStep(step);
      setDrawerOpen(true);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedStep(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return { icon: "check_circle", color: "#6BCF7F" };
      case "in-progress":
        return { icon: "radio_button_checked", color: "#4ECDC4" };
      case "locked":
        return { icon: "lock", color: "#95A5A6" };
      default:
        return { icon: "circle", color: "#95A5A6" };
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case "BASIC":
        return "#6BCF7F";
      case "ADVANCED":
        return "#4ECDC4";
      case "ULTIMATE":
        return "#FFD700";
      default:
        return "#95A5A6";
    }
  };

  return (
    <>
      <Card
        sx={(theme) => ({
          height: "100%",
          borderRadius: theme.borders.borderRadius.lg,
          boxShadow: theme.boxShadows.md,
          backgroundColor: theme.functions.rgba(theme.palette.background.default, 0.8),
        })}
      >
        <MDBox p={3}>
          <MDBox display="flex" alignItems="center" mb={3}>
            <Icon sx={{ fontSize: "1.5rem", mr: 1, color: "text" }}>timeline</Icon>
            <MDTypography variant="h5" fontWeight="bold" color="dark">
              Bootcamp Pathway
            </MDTypography>
          </MDBox>

          <MDBox>
            {pathway.map((step, index) => {
              const statusInfo = getStatusIcon(step.status);
              const isLast = index === pathway.length - 1;

              return (
                <MDBox key={step.id} position="relative" mb={isLast ? 0 : 2}>
                  <MDBox
                    display="flex"
                    gap={2}
                    sx={{
                      cursor: step.status !== "locked" ? "pointer" : "not-allowed",
                      opacity: step.status === "locked" ? 0.6 : 1,
                      transition: "opacity 0.2s ease",
                      "&:hover": step.status !== "locked" ? {
                        opacity: 0.8,
                      } : {},
                    }}
                    onClick={() => handleStepClick(step)}
                  >
                    {/* Icon and Connector */}
                    <MDBox display="flex" flexDirection="column" alignItems="center">
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        width={50}
                        height={50}
                        borderRadius="50%"
                        sx={{
                          backgroundColor:
                            step.status === "completed"
                              ? `${statusInfo.color}20`
                              : step.status === "in-progress"
                              ? `${statusInfo.color}20`
                              : "#F5F5F5",
                          border: `2px solid ${statusInfo.color}`,
                          zIndex: 2,
                        }}
                      >
                        <Icon
                          sx={{
                            fontSize: "1.5rem",
                            color: statusInfo.color,
                          }}
                        >
                          {statusInfo.icon}
                        </Icon>
                      </MDBox>
                      {!isLast && (
                        <MDBox
                          width="2px"
                          height="80px"
                          sx={{
                            backgroundColor:
                              step.status === "completed"
                                ? statusInfo.color
                                : "#E0E0E0",
                            mt: 0.5,
                          }}
                        />
                      )}
                    </MDBox>

                    {/* Content */}
                    <MDBox flex={1} pt={0.5}>
                      <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Chip
                          label={step.level}
                          size="small"
                          sx={{
                            backgroundColor: getLevelColor(step.level),
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "0.65rem",
                          }}
                        />
                        <MDTypography variant="body2" fontWeight="bold" color="dark">
                          Step {step.step}: {step.title}
                        </MDTypography>
                        {step.status === "completed" && step.completedDate && (
                          <MDTypography variant="caption" color="text">
                            ({new Date(step.completedDate).toLocaleDateString()})
                          </MDTypography>
                        )}
                      </MDBox>
                      <MDTypography variant="caption" color="text" mb={1}>
                        {step.description}
                      </MDTypography>
                      {step.status === "in-progress" && step.progress && (
                        <MDBox>
                          <MDProgress
                            value={step.progress}
                            color="info"
                            sx={(theme) => ({
                              height: "6px",
                              borderRadius: theme.borders.borderRadius.sm,
                            })}
                          />
                        </MDBox>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>
              );
            })}
          </MDBox>
        </MDBox>
      </Card>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 400 },
            p: 3,
          },
        }}
      >
        {selectedStep && (
          <MDBox>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <MDTypography variant="h5" fontWeight="bold" color="dark">
                {selectedStep.title}
              </MDTypography>
              <IconButton onClick={handleCloseDrawer}>
                <Icon>close</Icon>
              </IconButton>
            </MDBox>

            <MDTypography variant="body2" color="text" mb={3}>
              {selectedStep.description}
            </MDTypography>

            {/* Objectives */}
            {selectedStep.objectives && selectedStep.objectives.length > 0 && (
              <MDBox mb={3}>
                <MDTypography variant="h6" fontWeight="bold" color="dark" mb={1.5}>
                  Objectives
                </MDTypography>
                {selectedStep.objectives.map((objective, index) => (
                  <MDBox key={index} display="flex" alignItems="flex-start" gap={1} mb={1}>
                    <Icon sx={{ fontSize: "1rem", color: "info.main", mt: 0.25 }}>check</Icon>
                    <MDTypography variant="body2" color="text">
                      {objective}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Quizzes */}
            {selectedStep.quizzes && selectedStep.quizzes.length > 0 && (
              <MDBox mb={3}>
                <MDTypography variant="h6" fontWeight="bold" color="dark" mb={1.5}>
                  Quizzes
                </MDTypography>
                {selectedStep.quizzes.map((quiz, index) => (
                  <MDBox key={index} mb={1}>
                    <MDTypography variant="body2" color="text">
                      • {quiz}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            )}

            {/* Case Discussions */}
            {selectedStep.caseDiscussions && selectedStep.caseDiscussions.length > 0 && (
              <MDBox mb={3}>
                <MDTypography variant="h6" fontWeight="bold" color="dark" mb={1.5}>
                  Case Discussions
                </MDTypography>
                {selectedStep.caseDiscussions.map((case_, index) => (
                  <MDBox key={index} mb={1}>
                    <MDTypography variant="body2" color="text">
                      • {case_}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            )}

            {/* Live Sessions */}
            {selectedStep.liveSessions && selectedStep.liveSessions.length > 0 && (
              <MDBox>
                <MDTypography variant="h6" fontWeight="bold" color="dark" mb={1.5}>
                  Live Sessions
                </MDTypography>
                {selectedStep.liveSessions.map((session, index) => (
                  <MDBox key={index} mb={1.5} p={1.5} borderRadius="md" sx={{ backgroundColor: "#F5F5F5" }}>
                    <MDTypography variant="body2" fontWeight="bold" color="dark">
                      {session.title}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {new Date(session.date).toLocaleDateString()} at {session.time}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            )}
          </MDBox>
        )}
      </Drawer>
    </>
  );
}

export default BootcampPathwayTimeline;
